import { useEffect, useState } from "react";
import {
    fetchOccurrencesByGroupIndicators,
    IInProgressIndicators
} from "../api/fetchIndicators";
import { getOccurrenceStepLabelByStepValue, OccurrenceStepEnum, OccurrenceStepLabelEnum } from "../enums/occurrenceStep";
import { fetchOccurenceListWithTimeSpentForIndicators, fetchQuantityOccurrenceChartAnalytics } from "../api/fetchOccurence";
import { getChassisListFromAssetIds } from "../utils/assetUtils";
import useAssets from "./useAssets";

function useIndicators(persona: 'TOWER' | 'MANAGER' | 'CONSULTANT' | 'PARTNERSHIP', setChassisList: (chassisList: string[]) => void, assetIds?: string[]) {
    const { allAssets } = useAssets();

    const [showIndicators, setShowIndicators] = useState(false);
    const [loading, setLoading] = useState(true);

    const [selectedMetric, setSelectedMetric] = useState<"average" | "quantity">("average");

    const [allOccurrenceIdList, setAllOccurrenceIdList] = useState<string[]>([]);
    const [allOccurrenceTimeSpentList, setAllOccurrenceTimeSpentList] = useState<{ occurrenceId: string, timeSpent: number }[]>([]);
    const [filteredOccurrenceIdList, setFilteredOccurrenceIdList] = useState<string[]>([]);

    const [inProgress, setInProgress] = useState<string | undefined>(undefined);
    const [inProgressIndicators, setInProgressIndicators] = useState<{ name: string, qtd: number, averageTime: number, occurrenceIds: string[], chassisList: string[] }[]>([]);
    const [inProgressTotals, setInProgressTotals] = useState<IInProgressIndicators>({ finishedQtd: 0, finishedAverageTime: 0, inProgressQtd: 0, inProgressAverageTime: 0, occurrenceIds: [], chassisList: [] });

    const [customer, setCustomer] = useState<string | undefined>(undefined);
    const [customerIndicators, setCustomerIndicators] = useState<{ name: string, qtd: number, averageTime: number, occurrenceIds: string[], chassisList: string[] }[]>([]);

    const [model, setModel] = useState<string | undefined>(undefined);
    const [modelIndicators, setModelIndicators] = useState<{ name: string, qtd: number, averageTime: number, occurrenceIds: string[], chassisList: string[] }[]>([]);

    const [emissionStandard, setEmissionStandard] = useState<string | undefined>(undefined);
    const [emissionStandardIndicators, setEmissionStandardIndicators] = useState<{ name: string, qtd: number, averageTime: number, occurrenceIds: string[], chassisList: string[] }[]>([]);

    const [state, setState] = useState<string | undefined>(undefined);
    const [stateIndicators, setStateIndicators] = useState<{ name: string, qtd: number, averageTime: number, occurrenceIds: string[], chassisList: string[] }[]>([]);

    const [city, setCity] = useState<string | undefined>(undefined);
    const [cityIndicators, setCityIndicators] = useState<{ name: string, qtd: number, averageTime: number, occurrenceIds: string[], chassisList: string[] }[]>([]);

    const [groupIndicators, setGroupIndicators] = useState<{ group: string, items: { name: string, qtd: number, averageTime: number, chassisList: string[], occurrenceIds: string[] }[], total: number, averageTime: number }[]>([]);

    useEffect(() => {
        const fetchData = async (chassisListFilter: string[]) => {
            setLoading(true);

            let stepType = undefined;
            if (inProgress) {
                const stepKey = Object.keys(OccurrenceStepLabelEnum).find(key => OccurrenceStepLabelEnum[key as keyof typeof OccurrenceStepLabelEnum] === inProgress);
                stepType = stepKey ? OccurrenceStepEnum[stepKey as keyof typeof OccurrenceStepLabelEnum] : undefined;
            }

            const allOccurrencesWithTimeSpent = await fetchOccurenceListWithTimeSpentForIndicators({ chassisListFilter }, persona);
            const [occurrenceWithTimeSpentList, finalizedOccurrenceWithTimeSpentList] = [allOccurrencesWithTimeSpent.current, allOccurrencesWithTimeSpent.finalized];

            setAllOccurrenceTimeSpentList(occurrenceWithTimeSpentList.map(el => ({ occurrenceId: el.occurrenceId, timeSpent: el.timeSpent })));

            const customers = [...new Set(occurrenceWithTimeSpentList.map(el => el.customer))];
            const models = [...new Set(occurrenceWithTimeSpentList.map(el => el.model))];
            const emissionStandards = [...new Set(occurrenceWithTimeSpentList.map(el => el.legislation))];
            const states = [...new Set(occurrenceWithTimeSpentList.map(el => el.state))];
            const cities = [...new Set(occurrenceWithTimeSpentList.map(el => el.city))];

            setCustomerIndicators(customers.map(customer => {
                const occurrencesFound = occurrenceWithTimeSpentList.filter(el => el.customer === customer);
                return { name: customer, qtd: occurrencesFound.length, averageTime: occurrencesFound.reduce((acc, el) => acc + el.timeSpent, 0) / occurrencesFound.length, occurrenceIds: occurrencesFound.map(el => el.occurrenceId), chassisList: [] };
            }));
            setModelIndicators(models.map(model => {
                const occurrencesFound = occurrenceWithTimeSpentList.filter(el => el.model === model);
                return { name: model, qtd: occurrencesFound.length, averageTime: occurrencesFound.reduce((acc, el) => acc + el.timeSpent, 0) / occurrencesFound.length, occurrenceIds: occurrencesFound.map(el => el.occurrenceId), chassisList: [] };
            }));
            setEmissionStandardIndicators(emissionStandards.map(emissionStandard => {
                const occurrencesFound = occurrenceWithTimeSpentList.filter(el => el.legislation === emissionStandard);
                return { name: emissionStandard, qtd: occurrencesFound.length, averageTime: occurrencesFound.reduce((acc, el) => acc + el.timeSpent, 0) / occurrencesFound.length, occurrenceIds: occurrencesFound.map(el => el.occurrenceId), chassisList: [] };
            }));
            setStateIndicators(states.map(state => {
                const occurrencesFound = occurrenceWithTimeSpentList.filter(el => el.state === state);
                return { name: state, qtd: occurrencesFound.length, averageTime: occurrencesFound.reduce((acc, el) => acc + el.timeSpent, 0) / occurrencesFound.length, occurrenceIds: occurrencesFound.map(el => el.occurrenceId), chassisList: [] };
            }));
            setCityIndicators(cities.map(city => {
                const occurrencesFound = occurrenceWithTimeSpentList.filter(el => el.city === city);
                return { name: city, qtd: occurrencesFound.length, averageTime: occurrencesFound.reduce((acc, el) => acc + el.timeSpent, 0) / occurrencesFound.length, occurrenceIds: occurrencesFound.map(el => el.occurrenceId), chassisList: [] };
            }));

            setInProgressTotals({
                finishedQtd: finalizedOccurrenceWithTimeSpentList.length,
                finishedAverageTime: finalizedOccurrenceWithTimeSpentList.reduce((acc, el) => acc + el.timeSpent, 0) / finalizedOccurrenceWithTimeSpentList.length,
                inProgressQtd: occurrenceWithTimeSpentList.length,
                inProgressAverageTime: occurrenceWithTimeSpentList.reduce((acc, el) => acc + el.timeSpent, 0) / occurrenceWithTimeSpentList.length,
                occurrenceIds: occurrenceWithTimeSpentList.map(el => el.occurrenceId),
                chassisList: [],
            })

            const uniqueDealershipList = [...new Set(occurrenceWithTimeSpentList.map(el => el.dealershipName))];
            const dealershipGroupDns = uniqueDealershipList
                .map(name => ({ name, dns: [...new Set(occurrenceWithTimeSpentList.filter(el => el.dealershipName === name).map(el => el.dn))] }));

            setGroupIndicators(uniqueDealershipList.map(dealership => {
                const dealershipGroupDnsFound = dealershipGroupDns.find(el => el.name === dealership) ?? { name: dealership, dns: [] };
                return {
                    group: dealership,
                    items: dealershipGroupDnsFound.dns
                        .map(dn => {
                            const occurrencesByDnFound = occurrenceWithTimeSpentList.filter(el => el.dn === dn);
                            return {
                                name: `${dealership} - ${dn}`,
                                qtd: occurrencesByDnFound.length,
                                averageTime: occurrencesByDnFound.reduce((acc, el) => acc + el.timeSpent, 0) / occurrencesByDnFound.length,
                                occurrenceIds: occurrencesByDnFound.map(el => el.occurrenceId),
                                chassisList: [],
                            };
                        }),
                    total: 0,
                    averageTime: 0,
                }
            }));

            const inProgressIndicators = await fetchQuantityOccurrenceChartAnalytics({ chassisListFilter }, persona, false);

            const inProgressValues = Object.entries(inProgressIndicators.monthly)
                .map(([key, list]) => (
                    {
                        key,
                        qtd: list.reduce((acc, el) => acc + el.quantity, 0),
                        averageTime: 0,
                        occurrenceIds: list.flatMap(el => el.occurrenceUuidList),
                        chassisList: list.flatMap(el => el.chassisList)
                    }
                ));

            setInProgressIndicators(Object.values(OccurrenceStepEnum)
                .map(key => {
                    const value = inProgressValues.find(el => el.key === key);
                    return {
                        name: getOccurrenceStepLabelByStepValue(key),
                        qtd: value?.qtd ?? 0,
                        averageTime: occurrenceWithTimeSpentList.filter(el => value?.occurrenceIds.includes(el.occurrenceId)).reduce((acc, el) => acc + el.timeSpent, 0) / (value?.qtd ?? 1),
                        occurrenceIds: value?.occurrenceIds ?? [],
                        chassisList: value?.chassisList ?? []
                    }
                }));

            // Get all occurrence ids using customer
            const allOccurrenceIds = customers.flatMap(customer => {
                const occurrencesFound = occurrenceWithTimeSpentList.filter(el => el.customer === customer);
                return occurrencesFound.map(el => el.occurrenceId);
            })

            setAllOccurrenceIdList(allOccurrenceIds);
            setFilteredOccurrenceIdList(allOccurrenceIds);

            setLoading(false);
        }

        if (assetIds && assetIds.length > 0) {
            fetchData(
                ['CONSULTANT', 'PARTNERSHIP'].includes(persona) ? [] :
                    getChassisListFromAssetIds(allAssets, assetIds)
            );
        }
    }, [assetIds]);

    const filterCharts = (chart: "inProgress" | "customer" | "model" | "emissionStandard" | "state" | "city", value: string) => {
        let localFilteredOccurrenceIdList: string[] = [];
        // let localFilteredChassisList: string[] = [];
        if (chart === "inProgress") {
            setInProgress(value === inProgress ? undefined : value);
            localFilteredOccurrenceIdList = value === inProgress ? allOccurrenceIdList : (inProgressIndicators.find(el => el.name === value)?.occurrenceIds || []);
            // localFilteredChassisList = value === inProgress ? allOccurrenceIdList : ((inProgressIndicators.find(el => el.name === value)?.chassisList) || []);
            setCustomer(undefined);
            setModel(undefined);
            setEmissionStandard(undefined);
            setState(undefined);
            setCity(undefined);
        }
        if (chart === "customer") {
            setInProgress(undefined);
            setCustomer(value === customer ? undefined : value);
            localFilteredOccurrenceIdList = value === customer ? allOccurrenceIdList : (customerIndicators.find(el => el.name === value)?.occurrenceIds || []);
            // localFilteredChassisList = value === customer ? allOccurrenceIdList : (customerIndicators.find(el => el.name === value)?.chassisList || []);
            setModel(undefined);
            setEmissionStandard(undefined);
            setState(undefined);
            setCity(undefined);
        }
        if (chart === "model") {
            setInProgress(undefined);
            setModel(value === model ? undefined : value);
            localFilteredOccurrenceIdList = value === model ? allOccurrenceIdList : (modelIndicators.find(el => el.name === value)?.occurrenceIds || []);
            // localFilteredChassisList = value === model ? allOccurrenceIdList : (modelIndicators.find(el => el.name === value)?.chassisList || []);
            setCustomer(undefined);
            setEmissionStandard(undefined);
            setState(undefined);
            setCity(undefined);
        }
        if (chart === "emissionStandard") {
            setInProgress(undefined);
            setEmissionStandard(value === emissionStandard ? undefined : value);
            localFilteredOccurrenceIdList = value === emissionStandard ? allOccurrenceIdList : (emissionStandardIndicators.find(el => el.name === value)?.occurrenceIds || []);
            // localFilteredChassisList = value === emissionStandard ? allOccurrenceIdList : (emissionStandardIndicators.find(el => el.name === value)?.chassisList || []);
            setCustomer(undefined);
            setModel(undefined);
            setState(undefined);
            setCity(undefined);
        }
        if (chart === "state") {
            setInProgress(undefined);
            setState(value === state ? undefined : value);
            localFilteredOccurrenceIdList = value === state ? allOccurrenceIdList : (stateIndicators.find(el => el.name === value)?.occurrenceIds || []);
            // localFilteredChassisList = value === state ? allOccurrenceIdList : (stateIndicators.find(el => el.name === value)?.chassisList || []);
            setCustomer(undefined);
            setModel(undefined);
            setEmissionStandard(undefined);
            setCity(undefined);
        }
        if (chart === "city") {
            setInProgress(undefined);
            setCity(value === city ? undefined : value);
            localFilteredOccurrenceIdList = value === city ? allOccurrenceIdList : (cityIndicators.find(el => el.name === value)?.occurrenceIds || []);
            // localFilteredChassisList = value === city ? allOccurrenceIdList : (cityIndicators.find(el => el.name === value)?.chassisList || []);
            setCustomer(undefined);
            setModel(undefined);
            setEmissionStandard(undefined);
            setState(undefined);
        }

        setFilteredOccurrenceIdList(localFilteredOccurrenceIdList);
        // setChassisList(localFilteredChassisList);
    }

    const getInProgressTotals = (): { totals: number, averageTime: number } => {
        // Utilizando cityIndicators (indicadores de chamados por cidade) para obter os dados de chamados em andamento
        const inProgressItems = customerIndicators
            .map((el) => {
                const filteredOccurrenceIds = el.occurrenceIds.filter((occurrenceId) => filteredOccurrenceIdList.includes(occurrenceId));
                return {
                    name: el.name,
                    qtd: filteredOccurrenceIds.length,
                }
            })
            .filter(Boolean);

        const totals = inProgressItems.reduce((acc, el) => acc + el.qtd, 0);
        const averageTime = filteredOccurrenceIdList.reduce((acc, occurrenceId) => acc + (allOccurrenceTimeSpentList.find((el) => el.occurrenceId === occurrenceId)?.timeSpent || 0), 0) / totals;

        return {
            totals,
            averageTime,
        }
    }

    return {
        loading,
        showIndicators,
        setShowIndicators,

        inProgressIndicators,
        inProgressTotals,

        selectedMetric,
        setSelectedMetric,

        inProgress,
        customer,
        model,
        emissionStandard,
        state,
        city,

        customerIndicators,
        modelIndicators,
        emissionStandardIndicators,
        stateIndicators,
        cityIndicators,
        groupIndicators,

        filterCharts,
        filteredOccurrenceIdList,

        getInProgressTotals,
        allOccurrenceTimeSpentList,
    }
}

export default useIndicators;